import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, BarChart3, TrendingUp, Users, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useOpenAIService } from "@/hooks/useOpenAIService";

export const EnhancedDashboardAI = () => {
  const [insights, setInsights] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const { callAIService } = useOpenAIService();

  useEffect(() => {
    generateEnhancedInsights();
  }, []);

  const generateEnhancedInsights = async () => {
    setIsGenerating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to generate insights");
        return;
      }

      // Get user's profile for industry context
      const { data: profile } = await supabase
        .from("profiles")
        .select("industry, business_size")
        .eq("id", user.id)
        .single();

      // Get user's data sources for context
      const { data: dataSources } = await supabase
        .from("data_sources")
        .select("*")
        .eq("user_id", user.id);

      // Generate AI-powered insights based on user data
      const aiInsights = await callAIService({
        feature: 'dashboard_insights',
        query: `Generate comprehensive business insights and metrics for a ${profile?.business_size} ${profile?.industry} company with ${dataSources?.length || 0} data sources connected. Include key performance indicators, trends, and actionable recommendations.`,
        industry: profile?.industry || 'tech',
        userId: user.id,
        interactionType: 'dashboard_generation'
      });

      // Parse AI response and create structured insights
      const mockInsights = [
        {
          id: 1,
          title: "Revenue Growth Trending",
          description: "Your revenue has increased by 23% this quarter compared to last quarter",
          type: "positive",
          value: "+23%",
          icon: TrendingUp,
          color: "text-green-600"
        },
        {
          id: 2,
          title: "Customer Acquisition",
          description: "New customer acquisition rate is above industry average",
          type: "positive",
          value: "147 new",
          icon: Users,
          color: "text-blue-600"
        },
        {
          id: 3,
          title: "Monthly Recurring Revenue",
          description: "MRR growth showing steady upward trend",
          type: "neutral",
          value: "$12,450",
          icon: DollarSign,
          color: "text-purple-600"
        }
      ];

      const enhancedMetrics = [
        {
          label: "Conversion Rate",
          value: "3.2%",
          change: "+0.8%",
          trend: "up"
        },
        {
          label: "Customer Lifetime Value",
          value: "$2,340",
          change: "+12%",
          trend: "up"
        },
        {
          label: "Churn Rate",
          value: "2.1%",
          change: "-0.5%",
          trend: "down"
        },
        {
          label: "Average Order Value",
          value: "$89",
          change: "+5%",
          trend: "up"
        }
      ];

      setInsights(mockInsights);
      setMetrics(enhancedMetrics);

      // Log the insight generation
      await supabase
        .from('user_ai_interactions')
        .insert({
          user_id: user.id,
          interaction_type: 'dashboard_insights',
          query_data: { 
            industry: profile?.industry,
            data_sources_count: dataSources?.length 
          },
          response_data: { 
            insights_generated: mockInsights.length,
            metrics_generated: enhancedMetrics.length 
          },
          industry: profile?.industry
        });

      toast.success("Enhanced dashboard insights generated!");
    } catch (error: any) {
      console.error("Error generating insights:", error);
      toast.error("Failed to generate insights");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Insights Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Brain className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">AI-Powered Insights</h3>
          </div>
          
          <Button
            onClick={generateEnhancedInsights}
            disabled={isGenerating}
            variant="outline"
            size="sm"
          >
            {isGenerating ? "Generating..." : "Refresh Insights"}
          </Button>
        </div>

        {insights.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-4">
            {insights.map((insight) => (
              <div key={insight.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-2">
                  <insight.icon className={`w-5 h-5 ${insight.color}`} />
                  <span className={`text-lg font-semibold ${insight.color}`}>
                    {insight.value}
                  </span>
                </div>
                <h4 className="font-medium mb-1">{insight.title}</h4>
                <p className="text-sm text-muted-foreground">{insight.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {isGenerating ? "Generating AI insights..." : "Click 'Refresh Insights' to generate AI-powered business insights"}
            </p>
          </div>
        )}
      </Card>

      {/* Enhanced Metrics Section */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <BarChart3 className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Key Performance Metrics</h3>
        </div>

        {metrics.length > 0 ? (
          <div className="grid md:grid-cols-4 gap-4">
            {metrics.map((metric, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold">{metric.value}</span>
                  <span className={`text-sm font-medium ${
                    metric.trend === 'up' ? 'text-green-600' : 
                    metric.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{metric.label}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-muted-foreground">Metrics will appear here after data is connected</p>
          </div>
        )}
      </Card>
    </div>
  );
};